import {
  parseTemplateString,
  TemplateStringType,
} from "@turo/ui/lib/text/TemplateStringParser";

import { useSimpleTranslate } from "../SimpleTranslateProvider";

/** @typedef {import("~/src/pages/locales/types").CommonTranslations} CommonTranslations */
/** @typedef {import("~/src/pages/education-complete").EducationCompleteTranslations} EducationCompleteTranslations */
/** @typedef {import("~/src/pages/otp-login-email").OtpLoginEmailTranslations} OtpLoginEmailTranslations */
/** @typedef {import("~/src/pages/otp-login-phone").OtpLoginPhoneTranslations} OtpLoginPhoneTranslations */
/** @typedef {import("~/src/pages/signup").SignupTranslations} SignupTranslations */

/**
 * Looks for functions as parameters. This indicates a translation contains
 * templates
 * @param {Object} parameters
 * @return {Boolean}
 */
const hasTemplateParameters = (parameters) =>
  parameters &&
  Object.values(parameters).some((value) => typeof value === "function");

/**
 * Builds the elements out of a template translation
 * @param {String} template
 * @param {Object} parameters
 * @return {Array}
 */
const renderTemplatedTranslation = (template, parameters) =>
  parseTemplateString(template).map(({ tag, text, type }) => {
    if (type === TemplateStringType.TYPE_TEXT) {
      return text;
    }

    const renderFunction = parameters[tag];
    const isFunction = typeof renderFunction === "function";
    return isFunction ? renderFunction(text) : text;
  });

/**
 * This component avoids the need to use `translate` calls directly.
 * It accepts many different param combinations:
 * - Scenario 1: message as an object with text
 * - Scenario 2: message as an object with a translation key
 * - Scenario 3: message as an object with a translation key and params
 * - Scenario 4: message as a translation key
 * - Scenario 5: message as an already translated text
 * - Scenario 6: message is an array for pluralization (e.g. )
 * Example:
 * ```
 * <TextMessage message={['You have only {count} car', 'You have {count} cars', 'count']} params={{count: 4}} />
 * ```
 * - Scenario 7: message as a translation key with params
 * @param {Object} props
 * @param {keyof (CommonTranslations & EducationCompleteTranslations & OtpLoginEmailTranslations & OtpLoginPhoneTranslations & SignupTranslations)} props.message
 * @param {Object} [props.params]
 * @return {JSX.Element}
 */
export default function TextMessage({ message, params = undefined }) {
  const translate = useSimpleTranslate();
  const {
    key: messageKey,
    message: messageText = "",
    params: messageParameters,
  } = message || {};
  const isMessagePluralizationArray =
    Array.isArray(message) && message.length === 3;

  // Message is not an object
  if (typeof message === "string") {
    // Scenario 1: template params are specified
    if (hasTemplateParameters(params)) {
      return renderTemplatedTranslation(translate(message), params);
    }

    // Scenario 2: the message was a translation key or a text. If it was a text, translate yields the same result
    /**
     * CAUTION: This is currently not accurate, `translate` does not support passing a params argument,
     * which has been the case for a long time in this repo. It just so happens that we don't have instances
     * getting to this part of the code.
     *
     * TODO: Clean this up to allow passing params as needed.
     */
    return translate(message, params);
  }

  // Message is an object
  // Scenario 3: message has template params
  if (hasTemplateParameters(messageParameters)) {
    return renderTemplatedTranslation(translate(messageKey), messageParameters);
  }

  // Scenario 4: message has a translation key
  if (messageKey) {
    /**
     * CAUTION: This is currently not accurate, `translate` does not support passing a params argument,
     * which has been the case for a long time in this repo. It just so happens that we don't have instances
     * getting to this part of the code.
     *
     * TODO: Clean this up to allow passing params as needed.
     */
    return translate(messageKey, messageParameters);
  }

  // Scenario 5: The message passed is a number for display. Turn it into a string and return it.
  if (typeof message === "number") {
    return String(message);
  }

  // Scenario 6: message is an array for pluralization
  if (isMessagePluralizationArray) {
    /**
     * CAUTION: This is currently not accurate, `translate` does not support passing a params argument,
     * which has been the case for a long time in this repo. It just so happens that we don't have instances
     * getting to this part of the code.
     *
     * TODO: Clean this up to allow passing params as needed.
     */
    return translate(message, params);
  }

  // Scenario 7: message has no key so it's already translated
  return messageText;
}
